import { computed, inject, Injectable, signal, WritableSignal } from '@angular/core';
import { Subject } from 'rxjs';
import { LocalStorageService } from '../../../../core/services/LocalStorageService';
import { CartItemTypes } from '../enums/CartItemTypes';
import { ICartItem } from '../interfaces/ICartItem';
import { ISelectedPosition } from '../interfaces/ISelectedPosition';
import { getCartItemDisplayTotal } from '../utils/cart-item-display-total';
import { CartStore } from './CartStore';

@Injectable({
  providedIn: 'root'
})
export class PaySeparatelyStore {

  public static readonly STORAGE_KEY = 'hiddenPositions';
  public static readonly MODE_STORAGE_KEY = 'isPaySeparatelyModeEnabled';

  public isPaySeparatelyModeEnabled = signal(false);
  public selectedPositionsChanged$ = new Subject<void>();
  private _hiddenPositions: WritableSignal<string[]> = signal([]);

  private readonly _cartStore = inject(CartStore);
  private readonly _localStorageService = inject(LocalStorageService);

  public readonly selectedItemsTotal = computed(() => {
    if (!this.isPaySeparatelyModeEnabled() || !this._cartStore.cart()) {
      return 0;
    }

    const otherItemsTotal = this.filteredItems()
      .filter(item => item.itemType !== CartItemTypes.Ticket
        && item.itemType !== CartItemTypes.Product
        && item.itemType !== CartItemTypes.Package
        && !(item.itemType === CartItemTypes.Voucher && item.quantity > 1))
      .reduce((sum, item) => sum + item.totalPrice, 0);

    return otherItemsTotal + this.selectedTicketsTotal() + this.selectedProductsAndPackagesTotal() + this.selectedVouchersTotal();
  });

  public readonly selectedVouchersTotal = computed(() => {
    if (!this.isPaySeparatelyModeEnabled() || !this._cartStore.cart()) {
      return 0;
    }

    return this.filteredItems()
      .filter(item => item.itemType === CartItemTypes.Voucher && item.quantity > 1)
      .reduce((sum, item) => {
        const hiddenPositions = this.getValue(item.itemId, item.position);
        const selectedCount = Math.max(0, item.quantity - hiddenPositions.length);
        return sum + (item.price * selectedCount);
      }, 0);
  });

  public readonly selectedProductsAndPackagesTotal = computed(() => {
    if (!this.isPaySeparatelyModeEnabled() || !this._cartStore.cart()) {
      return 0;
    }

    return this.filteredItems()
      .filter(item => item.itemType === CartItemTypes.Product || item.itemType === CartItemTypes.Package)
      .reduce((sum, item) => {
        const hiddenPositions = this.getValue(item.itemId, item.position);
        const selectedCount = Math.max(0, item.quantity - hiddenPositions.length);
        return sum + (item.price * selectedCount);
      }, 0);
  });

  public get hiddenPositions(): string[] {
    return this._hiddenPositions();
  }

  public readonly selectedTicketsTotal = computed(() => {
    if (!this.isPaySeparatelyModeEnabled() || !this._cartStore.cart()) {
      return 0;
    }

    const cart = this._cartStore.cart();
    const discount = cart.discount;

    return this.filteredItems()
      .filter(item => item.itemType === CartItemTypes.Ticket)
      .reduce((sum, item) => sum + getCartItemDisplayTotal(item, discount, cart.items), 0);
  });

  public readonly toPayAmount = computed(() => {
    const cart = this._cartStore.cart();

    if (!cart) return 0;

    if (this.isPaySeparatelyModeEnabled()) {
      return this.selectedItemsTotal();
    }
    return cart.totalPrice;
  });

  public readonly refundAmount = computed(() => {
    const amountToPay = this.isPaySeparatelyModeEnabled()
      ? this.toPayAmount()
      : this._cartStore.cart().totalPrice;
    const hasOverpayment = this._cartStore.cart().paidAmount > amountToPay;
    return hasOverpayment ? this._cartStore.cart().paidAmount - amountToPay : 0;
  });

  private readonly filteredItems = computed(() => {
    if (!this.isPaySeparatelyModeEnabled() || !this._cartStore.cart()) {
      return [];
    }

    return this._cartStore.cart().items.filter(item => {
      const hiddenPositions = this.getValue(item.itemId, item.position);
      const selectedCount = Math.max(0, item.quantity - hiddenPositions.length);
      return selectedCount > 0;
    });
  });

  public constructor() {
    const savedPositions = this.loadFromLocalStorage();

    if (savedPositions && typeof savedPositions === 'object') {
      this._hiddenPositions.set(savedPositions);
    }
  }

  public getSelectedPositions(): ISelectedPosition[] | null {
    if (!this.isPaySeparatelyModeEnabled() || !this._cartStore.cart()) {
      return null;
    }
    const selected: ISelectedPosition[] = [];

    this._cartStore.cart()?.items.forEach(item => {
      const records = this.getValue(item.itemId, item.position);
      const hiddenCount = records.length;
      const selectedCount = Math.max(0, item.quantity - hiddenCount);

      if (selectedCount > 0) {
        selected.push({ itemId: item.itemId, position: item.position, quantity: selectedCount });
      }
    });
    
    return selected.length > 0 ? selected : null;
  }

  public setAll(hiddenPositions: string[] = []): void {
    this._hiddenPositions.set(hiddenPositions);
    this.saveToLocalStorage();
    this.selectedPositionsChanged$.next();
  }

  public setNone(): void {
    const cart = this._cartStore.cart();

    if (!cart) {
      return;
    }

    const keys: string[] = [];

    for (const item of cart.items) {
      if (item.itemType === CartItemTypes.Product || item.itemType === CartItemTypes.Package || (item.itemType === CartItemTypes.Voucher && item.quantity > 1)) {
        for (let subIndex = 0; subIndex < item.quantity; subIndex++) {
          keys.push(this.getKey(item.itemId, item.position, subIndex));
        }
      } else {
        keys.push(this.getKey(item.itemId, item.position));
      }
    }

    this.setAll(keys);
  }

  public togglePositionState(item: ICartItem, isVisible: boolean, subIndex?: number): void {
    const isProductOrPackage = item.itemType === CartItemTypes.Product || item.itemType === CartItemTypes.Package;
    const isVoucherWithQuantity = item.itemType === CartItemTypes.Voucher && item.quantity > 1;

    if (!isProductOrPackage && !isVoucherWithQuantity) {
      const positions = isVisible ?
        this._hiddenPositions().filter(key => key !== this.getKey(item.itemId, item.position)) :
        [...this._hiddenPositions(), this.getKey(item.itemId, item.position)];

      this._hiddenPositions.set(positions);

      this.saveToLocalStorage();
      this.selectedPositionsChanged$.next();

      return;
    }

    if ((subIndex !== undefined && subIndex !== null) || item.quantity === 1) {
      this._hiddenPositions.update(positions => isVisible 
        ? positions.filter(key => key !== this.getKey(item.itemId, item.position, subIndex)) 
        : [...positions, this.getKey(item.itemId, item.position, subIndex)]);
    } else {
      for (let i = 0; i < item.quantity; i++) {
        this._hiddenPositions.update(positions => isVisible 
          ? positions.filter(key => key !== this.getKey(item.itemId, item.position, i)) 
          : [...positions, this.getKey(item.itemId, item.position, i)]);
      }
    }

    this.saveToLocalStorage();
    this.disablePaySeparatelyModeWhenNoHiddenPositions();
    this.enablePaySeparatelyModeWhenHiddenPositions();
    this.selectedPositionsChanged$.next();
  }

  public isHidden(itemId: number, position: number, subIndex?: number): boolean {
    const key = this.getKey(itemId, position, subIndex);
    return !!this._hiddenPositions()?.find(k => k === key);
  }

  public getValue(itemId: number, position: number): string[] {
    const allPositions = this._hiddenPositions();
    const result: string[] = [];
    const baseKey = this.getKey(itemId, position);

    allPositions.forEach(key => {
      const keyWithoutSuffix = key.split('--')[0];
      if (keyWithoutSuffix === baseKey) {
        result.push(key);
      }
    });

    return result;
  }

  public removeItemByKey(key: string): void {
    this._hiddenPositions.update(positions => positions.filter(k => k !== key));
    this.saveToLocalStorage();
    this.selectedPositionsChanged$.next();
  }

  public removeKeysForCartPosition(itemId: number, position: number): void {
    const baseKey = this.getKey(itemId, position);
    this._hiddenPositions.update(positions =>
      positions.filter(k => k !== baseKey && !k.startsWith(`${baseKey}--`))
    );
    this.saveToLocalStorage();
    this.selectedPositionsChanged$.next();
    this.disablePaySeparatelyModeWhenNoHiddenPositions();
  }

  public loadFromStorage(): void {
    const saved = this.loadFromLocalStorage();
    
    if (saved && typeof saved === 'object') {
      this._hiddenPositions.set(saved);
      this.selectedPositionsChanged$.next();
    }
  }

  public reset(): void {
    this.isPaySeparatelyModeEnabled.set(false);
    this._localStorageService.setItem(PaySeparatelyStore.MODE_STORAGE_KEY, false);
    this.setAll([]);
  }

  private getKey(itemId: number, position: number, subIndex?: number): string {
    return subIndex !== undefined && subIndex !== null ? `${itemId}_${position}--${subIndex}` : `${itemId}_${position}`;
  }

  private saveToLocalStorage(): void {
    try {
      localStorage.setItem(PaySeparatelyStore.STORAGE_KEY, JSON.stringify(this._hiddenPositions()));
    } catch (error) {
      console.error('Failed to save selected positions to localStorage:', error);
    }
  }

  private disablePaySeparatelyModeWhenNoHiddenPositions(): void {
    if (this._hiddenPositions().length === 0) {
      this.isPaySeparatelyModeEnabled.set(false);
    }
  }

  private enablePaySeparatelyModeWhenHiddenPositions(): void {
    if (this._hiddenPositions().length > 0) {
      this.isPaySeparatelyModeEnabled.set(true);
    }
  }

  private loadFromLocalStorage(): Array<string> | null {
    try {
      const val = localStorage.getItem(PaySeparatelyStore.STORAGE_KEY);
      return val ? JSON.parse(val) : null;
    } catch (error) {
      console.error('Failed to load selected positions from localStorage:', error);
      return null;
    }
  }
}
